import { Alert, Box, Button, Container, Snackbar, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import ChangelogFormDialog from '../../components/ChangelogManagement/ChangelogFormDialog.tsx';
import ChangelogsTable from '../../components/ChangelogManagement/ChangelogsTable.tsx';
import { useChangelogApi } from '../../services/api/useChangelog.api.ts';
import { ChangelogCreate } from '../../types/Changelog/ChangelogCreate.type.ts';

function ChangelogManagement() {
  const queryClient = useQueryClient();
  const { createChangelog, updateChangelog } = useChangelogApi();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const { mutate: createChangelogMutation, isPending: isCreating } = useMutation({
    mutationKey: ['createChangelog'],
    mutationFn: (changelogData: ChangelogCreate) => createChangelog(changelogData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changelogs'] });
      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      setDialogOpen(false);
      showSnackbar('Changelog created successfully!', 'success');
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to create changelog. Please try again.', 'error');
    },
  });

  const { mutate: updateChangelogMutation, isPending: isUpdating } = useMutation({
    mutationKey: ['updateChangelog'],
    mutationFn: ({ id, changelogData }: { id: number; changelogData: ChangelogCreate }) =>
      updateChangelog(id, changelogData),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['changelogs'] });
      queryClient.invalidateQueries({ queryKey: ['changelog', id] });
      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      setDialogOpen(false);
      showSnackbar('Changelog updated successfully!', 'success');
    },
    onError: (error) => {
      showSnackbar(error?.message || 'Failed to update changelog. Please try again.', 'error');
    },
  });

  const handleCreateClick = () => {
    setEditId(undefined);
    setDialogOpen(true);
  };

  const handleEditClick = (id: number) => {
    setEditId(id);
    setDialogOpen(true);
  };

  const handleSubmit = (changelogData: ChangelogCreate) => {
    if (editId != null) {
      updateChangelogMutation({ id: editId, changelogData });
    } else {
      createChangelogMutation(changelogData);
    }
  };

  return (
    <>
      <Container sx={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography
              variant='h5'
              sx={{
                fontWeight: 'bold',
              }}
            >
              Manage changelogs
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                marginTop: 2,
              }}
            >
              Create changelogs or view/edit/delete existing changelogs. Use the actions menu to update changelog
              details.
            </Typography>
          </Box>
          <Button variant='contained' disableElevation startIcon={<PlusIcon size={18} />} onClick={handleCreateClick}>
            Create changelog
          </Button>
        </Box>
        <ChangelogsTable snackbarData={showSnackbar} onEdit={handleEditClick} />
      </Container>
      <ChangelogFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUpdating}
        changelogId={editId}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ChangelogManagement;
